import { Cause, Effect, Exit, Scope } from "effect";

/** Retain order and identity; no structural equality or private cause access. */
export const failureReasons = <E>(...exits: readonly Exit.Exit<unknown, E>[]): readonly Cause.Reason<E>[] =>
  exits.flatMap(exit => Exit.isFailure(exit) ? exit.cause.reasons : []);

/** Close the operation's scope before projection, preserving every reason without
 * comparing private failures. Settlement remains inside the interruption mask. */
export const settleScoped = <A, E, R, Failure>(operation: Effect.Effect<A, E, R>,
  settle: <Value>(exit: Exit.Exit<Value, NoInfer<Failure>>) => Effect.Effect<Value, NoInfer<Failure>>,
  project: (failure: E) => Failure) => Effect.uninterruptibleMask(restore => Effect.gen(function* () {
  const scope = yield* Scope.make();
  const exit = yield* Effect.exit(restore(operation.pipe(Effect.provideService(Scope.Scope, scope))));
  const closed = yield* Effect.exit(Scope.close(scope, exit));
  return Exit.isFailure(closed) ? Exit.failCause(Cause.fromReasons(failureReasons<E>(exit, closed))) : exit;
}).pipe(Effect.flatMap(exit => settle(Exit.isFailure(exit)
  ? Exit.failCause(Cause.map(exit.cause, project)) : Exit.succeed(exit.value)))));
